/**
 * Webview Events Helper Functions
 *
 * Provides type-safe helper functions for sending and receiving
 * webview events throughout the application.
 */

import { useMemo } from 'react';

import { useWebviewBridge } from './webview-bridge-context';
import { type SetPullToRefreshEnabledPayload, WebviewSendEventType } from './webview-events.type';

/**
 * Typed hook for sending/receiving webview events.
 *
 * @example
 * ```tsx
 * const { sendPullToRefreshEnabled } = useWebviewEvents();
 * sendPullToRefreshEnabled(true);
 * ```
 */
export const useWebviewEvents = () => {
  const { send } = useWebviewBridge();

  return useMemo(
    () => ({
      /**
       * Tell native whether the pull-to-refresh gesture should be enabled.
       *
       * @param enabled - `true` when the main scrollable container is at the top,
       *                  `false` when the user has scrolled down in a main container
       */
      sendPullToRefreshEnabled: async (enabled: boolean): Promise<void> => {
        await send<SetPullToRefreshEnabledPayload>(WebviewSendEventType.SET_PULL_TO_REFRESH_ENABLED, {
          enabled,
        });
      },
    }),
    [send]
  );
};
